import styled from 'styled-components';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { ReadOnlyBanner, useAgentPermission } from '@entities/user';
import { riseIn } from '@shared/theme/animations';

/* Оболочка агента ОСНОВА.Прораб: левый рельс («Обзор») + заголовок и `<Outlet/>`.
   Веб-витрина — обзор для офиса; ввод данных ведётся прорабами в мобильном
   приложении. Пункты рельса добавляются по мере вывода модулей на фронт. */

const Shell = styled.div`
  max-width: 1320px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 232px minmax(0, 1fr);
  gap: ${({ theme }) => theme.spacing(7)};
  align-items: start;

  @media (max-width: 920px) {
    grid-template-columns: minmax(0, 1fr);
    gap: ${({ theme }) => theme.spacing(5)};
  }
`;

const Rail = styled.nav`
  position: sticky;
  top: ${({ theme }) => theme.spacing(4)};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(0.5)};

  @media (max-width: 920px) {
    position: static;
    flex-direction: row;
    overflow-x: auto;
    gap: ${({ theme }) => theme.spacing(2)};
    padding-bottom: ${({ theme }) => theme.spacing(2)};
  }
`;

const Brand = styled(Link)`
  font-size: 17px;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: ${({ theme }) => theme.colors.text.primary};
  text-decoration: none;
  margin-bottom: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(2)}`};

  & .accent {
    color: ${({ theme }) => theme.colors.primary};
    font-weight: 800;
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadow.focus};
    border-radius: ${({ theme }) => theme.radius.sm};
  }

  @media (max-width: 920px) {
    display: none;
  }
`;

const RailLink = styled(Link)<{ $active: boolean }>`
  display: flex;
  align-items: baseline;
  gap: ${({ theme }) => theme.spacing(2)};
  font-size: 13.5px;
  font-weight: 600;
  text-decoration: none;
  white-space: nowrap;
  padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(3)}`};
  border-radius: ${({ theme }) => theme.radius.md};
  border-left: 2px solid ${({ theme, $active }) => ($active ? theme.colors.primary : 'transparent')};
  background: ${({ theme, $active }) => ($active ? theme.colors.primarySoft : 'transparent')};
  color: ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.text.secondary)};
  transition:
    background ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut},
    color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceHover};
    color: ${({ theme }) => theme.colors.text.primary};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }

  @media (max-width: 920px) {
    border-left: none;
    border: 1px solid
      ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.border)};
    border-radius: ${({ theme }) => theme.radius.pill};
  }
`;

const Main = styled.div`
  min-width: 0;
`;

const Header = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing(6)};
  animation: ${riseIn} 420ms ${({ theme }) => theme.motion.easeOut} both;
`;

const Title = styled.h1`
  margin: 0;
  font-size: 26px;
  font-weight: 700;
  letter-spacing: -0.02em;

  & .accent {
    color: ${({ theme }) => theme.colors.primary};
    font-weight: 800;
  }
`;

const Subtitle = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 15px;
  margin-top: ${({ theme }) => theme.spacing(2)};
  max-width: 760px;
`;

/**
 * Оболочка агента ОСНОВА.Прораб (`/agents/field`). Постоянный левый рельс
 * («Обзор») + контекстный заголовок и `<Outlet/>` для дочерних страниц.
 */
export function FieldLayout() {
  const location = useLocation();
  const { can_read, can_write } = useAgentPermission('field');
  const isOverview = location.pathname.split('/').filter(Boolean).length === 2;

  return (
    <Shell>
      <Rail aria-label="Навигация агента Прораб">
        <Brand to="/agents/field">
          ОСНОВА<span className="accent">.Прораб</span>
        </Brand>
        <RailLink
          to="/agents/field"
          $active={isOverview}
          aria-current={isOverview ? 'page' : undefined}
        >
          Обзор
        </RailLink>
      </Rail>

      <Main>
        <Header>
          <Title>
            ОСНОВА<span className="accent">.Прораб</span>
          </Title>
          <Subtitle>
            Полевой сбор данных с площадок: конструктив и заявки на освидетельствование (автосборка
            АОСР с материалами и фотодоказательствами), сменные рапорты, фотофиксация, приём ТТН,
            аномалии и дайджесты. Прорабы сдают личные рапорты и фотографии по назначенным фронтам в
            вебе или мобильном приложении; инженер проверяет выполнение.
          </Subtitle>
          <Subtitle>
            Нужна помощь? Откройте <Link to="/assistant">помощника ОСНОВЫ</Link> и попросите
            объяснить нужное действие по шагам.
          </Subtitle>
        </Header>
        {can_read && !can_write && <ReadOnlyBanner />}
        <Outlet />
      </Main>
    </Shell>
  );
}
